import { AliceSkillSettings } from 'client/model/settings';
import { UserAgreementOrDraft } from 'client/model/userAgreement';
import * as regionsKeyset from 'i18n/Region';

import { AliceApp, AliceSkill, Channel, InstallableApp, Owner, Skill, SmartHome } from '../model/skill';

import { YANDEX_COUNTRIES_SET } from './countries';

export const isIframe = () => window.self !== window.parent;

export const hrefIsExternal = new RegExp('^(?:[a-z]+:)?//', 'i');

export const popupWindow = (url: string, title: string, w: number, h: number) => {
    const left = screen.width / 2 - w / 2;
    const top = screen.height / 2 - h / 2;

    return window.open(
        url,
        title,
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${top}, left=${left}`,
    );
};

export function assignField<T, K extends string | number | symbol, V>(obj: T, field: K, value: V) {
    return Object.assign({}, obj, { [field]: value });
}

export function getKeys<T extends Record<string, unknown>>(obj: T) {
    return Object.keys(obj) as Array<keyof T>;
}

export function selectTextContent(domNode: HTMLElement) {
    const selection = window.getSelection();

    const range = document.createRange();
    range.selectNodeContents(domNode);

    selection!.removeAllRanges();
    selection!.addRange(range);
}

export function makeSafe<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
    return async (...args: A) => {
        try {
            return await fn(...args);
        } catch (e) {
            console.error(e);
        }
    };
}

export const ucfirst = (s: string) => s[0].toUpperCase() + s.slice(1);

export const isTouchSupported = () => 'ontouchstart' in window;

export const getSettingsEntity = <T extends AliceApp>(aliceApp: T, isDraft: boolean) => {
    return isDraft ? (aliceApp.draft as T['draft']) : aliceApp;
};

export const getSettingsWithUserAgreements = (
    aliceApp: AliceSkill | SmartHome,
    isDraft: boolean,
    userAgreements?: UserAgreementOrDraft[],
): AliceSkillSettings => {
    const settings = isDraft ? aliceApp.draft : aliceApp;
    return { ...settings, skillUserAgreements: userAgreements || [] };
};

export const getInstallableAppSettingsEntity = <T extends InstallableApp>(installableApp: T, isDraft: boolean) => {
    // ad-hoc: settings are propagated to <Settings> component, but for tvApp.draft we should also
    // propagate skill.packageName (because it should be modifiable until the first publication)
    if (isDraft) {
        return {
            ...installableApp.draft,
            packageName: (installableApp as InstallableApp).packageName,
        } as T['draft'] & { packageName: string };
    }
    return installableApp;
};

const showAppsUi = () => Boolean(__appConfig.user.featureFlags.enableAppsAllChannels);
export const showAppStoreUi = () => !getIsYangoRequest();
export const showAutoAppUi = () =>
    (Boolean(__appConfig.user.featureFlags.enableAutoApps) && showAppStoreUi()) || showAppsUi();

export const showQuickAppsUi = () =>
    Boolean(__appConfig.user.isYandexoid) || Boolean(__appConfig.app.showQuickAppsPageToAllUsers);

export const showSkillsRedesign = () =>
    Boolean(__appConfig.app.displaySkillsRedesign) || Boolean(__appConfig.user.featureFlags.showSkillsRedesignToUser);

export const getIsYangoRequest = () => Boolean(__appConfig.isYangoRequest);

export const getIsMultilingualSettingsEnabled = () =>
    getIsYangoRequest() || __appConfig.app.isMultilingualSettingsInYandexRuEnabled;

export const getShowMailAnnouncement = () => !getIsYangoRequest() || __appConfig.app.paskillsMailInYangoEnabled;

export const getQuasarUiUrl = () =>
    getIsYangoRequest() ? __appConfig.app.quasarUiYangoUrl : __appConfig.app.quasarUiUrl;

export const removeLeadingSlash = (value: string) => {
    return value.replace(/^\//, '');
};

export const shouldMarkAsProvidedByOtherCompany = (countryCode: keyof (typeof regionsKeyset)['en']) => {
    return (
        (getIsYangoRequest() && YANDEX_COUNTRIES_SET.has(countryCode)) ||
        (!getIsYangoRequest() && !YANDEX_COUNTRIES_SET.has(countryCode))
    );
};

export const isInstallableAppChannel = (
    channel: Channel,
): channel is Channel.AutoApp | Channel.TvApp | Channel.SmartScreenApp => {
    return channel === Channel.TvApp || channel === Channel.AutoApp || channel === Channel.SmartScreenApp;
};

export const isAliceSkillOrSmartHomeSkill = (skill: Skill): skill is AliceSkill | SmartHome =>
    skill.channel === Channel.AliceSkill || skill.channel === Channel.SmartHome;

export const isSmartHomeSkill = (skill: Skill): skill is SmartHome => skill.channel === Channel.SmartHome;

export const isInstallableApp = (app: InstallableApp | Skill): app is InstallableApp => {
    return isInstallableAppChannel(app.channel);
};

export const getIsAdmin = () => __appConfig.user.isAdmin;
export const getIsGlobalViewer = () => !!__appConfig.user.isGlobalViewer;
export const getAccessAsViewer = () => getIsAdmin() || getIsGlobalViewer();

export const getCanViewAsAdmin = () => getIsAdmin() || getIsGlobalViewer();
export const getCanEditAsAdmin = () => getIsAdmin();

export const getCanEditApp = (app: AliceApp) => {
    const appUserId = app?.user?.id || app?.userId;
    const isCurrentAppOwnerCommonUser = __appConfig.user.blackbox.uid === appUserId;
    const isCurrentAppOwnerPorgUser =
        app?.owner?.type === Owner.PORG && __appConfig.user.porgId === app.owner?.entityId;

    return isCurrentAppOwnerCommonUser || isCurrentAppOwnerPorgUser || getIsAdmin();
};

export const getIsUserPorg = () => Boolean(__appConfig.user?.porgName);

export const getIsAllowedFetchSkillDirectly = () => getIsAdmin() || showSkillsRedesign() || getIsGlobalViewer();
export const getCreateInstallableAppPorgModal = () => Boolean(__appConfig.app.createInstallableAppPorgModal);
export const getAdvancedVoiceControl = () => Boolean(__appConfig.app.advancedVoiceControl);

export const displayUserAgreements = () => __appConfig.user.featureFlags.enableUserAgreements || getCanViewAsAdmin();
